import Currency from '@modules/Currency.js'
import { useGtm } from '@gtm-support/vue-gtm';
import { useBasketStore } from '@stores/Portal/basket/index.js'

/**
 * Class responsible for firing GA4 events
 *
 * https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag
 *
 */

export default class GtmEvent {

    checkoutJourney = [
        'view_cart',
        'begin_checkout',
        'purchase'
    ]

    crossSellArea = null
    mappedItems = null
    basket = null

    constructor(
        {
            eventName,
            items = null,
            quantity = null,
            deletingVariantFromBasket = false,
            transactionId = null,
            paymentType = null,
            shipping = null,
            categoryListDetails = null,
            valueRequired = true,
            searchTerm = null,
            crossSellArea = null,
        }
    ) {
        this.gtm = useGtm()
        this.eventName = eventName
        this.basket = useBasketStore().basket
        this.items = ! Array.isArray(items) ? [items] : items
        this.quantity = quantity
        this.deletingVariantFromBasket = deletingVariantFromBasket
        this._transactionId = transactionId;
        this._paymentType = paymentType;
        this._shipping = shipping;
        this._categoryListDetails = categoryListDetails;
        this._valueRequired = valueRequired;
        this._searchTerm = searchTerm;
        // When set (e.g. banner modal view_promotion), apply to every item — do not rely on DOM.
        this._forcedCrossSellArea = crossSellArea;
    }

    /**
     * Fires the relevant gtm event
     *
     * @returns {GtmEvent}
     */
    trackGtmEvent() {
        this.mappedItems = this.gtmMappedItems()

        this.gtm.trackEvent(
            {
                event: this.eventName,
                trader_id: this.basket.account_number,
                currency: Currency.currency,
                value: this._valueRequired ? this.mappedItems.eventValue : null,
                item_list_name: this._categoryListDetails?.title, //Ignored if set at the item-level
                item_list_id: this._categoryListDetails?.id,
                transaction_id: this._transactionId,
                payment_type: this._paymentType,
                coupon: this.basket.promo? this.basket.promo.promoCode : null,
                items: this.mappedItems.items,
                shipping: this._shipping,
            }
        )

        return this
    }

    /**
     * Method responsible for mapping the custom page view event
     *
     */
    trackPageViewEvent(title, location)
    {
        this.$gtm.trackEvent(
            {
                event: this.eventName,
                page_title_custom: title,
                page_location_custom: location
            }
        )
    }

    /**
     * Fires GA4 view_search_results when product search returns successfully.
     *
     * @returns {GtmEvent}
     */
    trackViewSearchResultsEvent() {
        this.gtm.trackEvent({
            event: this.eventName,
            search_term: this._searchTerm,
        })

        return this
    }

    /**
     * Method responsible for mapping the item parameters and
     * works out the event value.
     *
     * @returns {{eventValue: number, items: {creative_name: *, quantity: *, item_id: *, item_category4: *, index: *, item_category5: *, promotion_id: *, item_name: *, item_category2: *, item_category3: *, item_brand: *, item_category: *, creative_slot: *, promotion_name: *, item_list_name: *, price: *, item_variant: *}[]}}
     */
    gtmMappedItems() {

        let eventValue = 0;

        let items = this.items.map(function(item, index) {
            let itemQty = this.getItemQty(item)

            eventValue += parseFloat(item.price) * itemQty

            this.setCrossSellArea(item)

            return {
                creative_name: this.crossSellArea?.creative_name,
                creative_slot: this.crossSellArea?.creative_slot,
                promotion_id: this.crossSellArea?.gtm_promotion_id,
                promotion_name: this.crossSellArea?.gtm_promotion_name,
                item_id: item.sku,
                item_name: item.title,
                index: index,
                item_brand: item.brand_title,
                item_category: item.categories?.item_category,
                item_category2: item.categories?.item_category2,
                item_category3: item.categories?.item_category3,
                item_category4: item.categories?.item_category4,
                item_category5: item.categories?.item_category5,
                item_variant: item.uom,
                item_list_name: this._categoryListDetails?.title || this.crossSellArea?.item_list_name,
                item_list_id: this._categoryListDetails?.id,
                price: item.price,
                quantity: itemQty
            }
        }, this)

        return {
            eventValue: Math.round(eventValue*100)/100,
            items: items
        }
    }

    /**
     *
     * @param item
     * @returns {null|*}
     */
    getItemQty(item) {
        return this.quantity ? this.quantity : item.basket_quantity
    }

    /**
     * Works out if item is being added from cross-sell area and sets it.
     *
     * @param item
     * @returns {null|any}
     */
    setCrossSellArea(item) {
        if (this._forcedCrossSellArea) {
            return this.crossSellArea = this._forcedCrossSellArea
        }

        if (
            item.cross_sell_area &&
            this.checkoutJourney.includes(this.eventName)
        ) {
            return this.crossSellArea = item.cross_sell_area
        }

        let grouping = document.querySelector("#gtm-cta-product-add-" + item.id)
            ?.closest('.product-grouping')
            ?.getAttribute('data-product-grouping')

        try {

            return this.crossSellArea = JSON.parse(grouping)

        } catch (e) {

            return this.crossSellArea = this._categoryListDetails ?
                {
                    creative_name: this._categoryListDetails.title,
                    creative_slot: this._categoryListDetails.title.toLowerCase().split(' ').join('_') + '_list',
                    item_list_name: this._categoryListDetails.title + ' List'
                } : null
        }
    }

    /**
     * Fires the select_promotion event if item added to the basket from cross-sell area.
     *
     * PLEASE NOTE - If an item has already been added from cross-sell area we don't want to override this,
     * we want to see where it was originally added.
     *
     * @param item
     * @returns {GtmEvent}
     */
    fireSelectPromotionEvent(item) {
        if ( this.eventName !== 'add_to_cart' || item.cross_sell_area ) return this

        this.setCrossSellArea(item)

        if ( this.crossSellArea?.gtm_promotion_id ) {
            this.gtm.trackEvent(
                {
                    event: 'select_promotion',
                    trader_id: this.basket.account_number,
                    items: this.gtmMappedItems().items,
                }
            )
        }

        return this
    }

    /**
     * Fires view_promotion / select_promotion for a banner creative (no product line items).
     * Uses the same promotion field names as CrossSellArea / product-grouping JSON.
     * When categoryListDetails is set (shop category pages), includes item_list_id / item_list_name
     * the same way as view_item_list.
     *
     * @param {object|null} promotionGrouping
     * @returns {GtmEvent}
     */
    trackPromotionCreativeEvent(promotionGrouping) {
        if (! promotionGrouping?.gtm_promotion_id) {
            return this
        }

        this.gtm.trackEvent({
            event: this.eventName,
            trader_id: this.basket.account_number,
            item_list_id: this._categoryListDetails?.id ?? null,
            item_list_name: this._categoryListDetails?.title ?? null,
            items: [
                {
                    creative_name: promotionGrouping.creative_name,
                    creative_slot: promotionGrouping.creative_slot,
                    promotion_id: promotionGrouping.gtm_promotion_id,
                    promotion_name: promotionGrouping.gtm_promotion_name,
                    item_list_id: this._categoryListDetails?.id ?? null,
                    item_list_name: this._categoryListDetails?.title ?? null,
                },
            ],
        })

        return this
    }
}
